// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddToListModal from "@insite/content-library/Components/AddToListModal";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartPageTemplate, { CartPageTemplateStyles } from "@werner/App/Components/Cart/CartPageTemplate";
import CheckoutMoveToStep from "@werner/App/Components/Checkout/CheckoutMoveToStep";
import SectionTitle, { SectionTitleStyles } from "@werner/App/Components/Common/SectionTitle";
import XngZone from "@werner/App/Components/Common/XngZone";
import { checkoutReviewSectionTitle } from "@werner/App/SharedStyle/CheckoutPageStyles";
import { CheckoutStepper } from "@werner/App/Widgets/CheckoutStepper/CheckoutStepper";
import { CHECKOUT_STEPS } from "@werner/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { StepperContext } from "@werner/App/Widgets/CheckoutStepper/StepperContext";
import { iconLinkCommonStyles, mergeWithCss, xScreen } from "@werner/XDL/Utilities";
import React, { FC, useState } from "react";
import { useContext } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

type Props = OwnProps;

export interface CheckoutPageContainerStyles extends CartPageTemplateStyles {
    headerTitle?: TypographyPresentationProps;
    leftSide?: InjectableCss;
    leftBottomSide?: InjectableCss;
    productListWrapper?: InjectableCss;
    productListExtraChildWrapper?: InjectableCss;
    productListViewCollapseLink?: LinkPresentationProps;
    productListViewExpandLink?: LinkPresentationProps;
    sectionTitle?: SectionTitleStyles;
}

export const checkoutPageContainerStyles: CheckoutPageContainerStyles = {
    headerTitle: {
        variant: "h1",
        css: css`
            margin-bottom: 24px;
        `,
    },
    headerSection: {
        css: css`
            width: 100%;
            & > ${TypographyStyle} {
                &:not(:last-child) {
                    margin-bottom: 20px;
                    ${xScreen("lte", "md")} {
                        margin-bottom: 24px;
                    }
                    ${xScreen("lte", "sm")} {
                        margin-bottom: 16px;
                    }
                }
            }
        `,
    },

    productListExtraChildWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 40px;
        `,
    },
    productListViewCollapseLink: {
        ...iconLinkCommonStyles("Minimize"),
    },
    productListViewExpandLink: {
        ...iconLinkCommonStyles("Maximize"),
    },
    productListWrapper: {
        css: css`
            margin-top: 70px;
            width: 100%;
        `,
    },
    sectionTitle: {
        ...checkoutReviewSectionTitle,
    },
    container: {
        css: css`
            display: flex;
            ${xScreen("lt", "md")} {
                flex-direction: row;
            }
            ${xScreen("lt", "sm")} {
                flex-direction: column;
            }
        `,
    },
};

const styles = checkoutPageContainerStyles;

const CheckoutPageContainer: FC<Props> = ({ id }) => {
    const { currentStep } = useContext(StepperContext);
    const isReview = currentStep === CHECKOUT_STEPS.REVIEW;
    return (
        <CartPageTemplate isReview={isReview} extendedStyles={styles}>
            <CartPageTemplate.MainSection>
                <XngZone
                    contentId={id}
                    zoneName="Content1"
                    hint="Zone For Step Content (Shipping container, Submit & Review container)"
                />
                {currentStep === CHECKOUT_STEPS.REVIEW && (
                    <StyledWrapper {...styles.productListWrapper}>
                        <SectionTitle
                            title={translate("Products")}
                            extraChild={<CheckoutMoveToStep moveToStep={CHECKOUT_STEPS.CART} />}
                            extendedStyles={styles.sectionTitle}
                        />
                        {createWidgetElement("CheckoutReviewAndSubmit/CheckoutReviewAndSubmitProductList", {
                            fields: {},
                        })}
                    </StyledWrapper>
                )}
                <AddToListModal />
            </CartPageTemplate.MainSection>
            <CartPageTemplate.CartTotalSection>
                <XngZone contentId={id} zoneName="Content2" hint="Zone For Checkout Total" />
                <CartPageTemplate.ActionsSection>
                    <XngZone contentId={id} zoneName="Content3" hint="Zone For Stepper Actions" />
                </CartPageTemplate.ActionsSection>
            </CartPageTemplate.CartTotalSection>
        </CartPageTemplate>
    );
};

const widgetModule: WidgetModule = {
    component: CheckoutPageContainer,
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        displayName: "Checkout Page Container",
        fieldDefinitions: [],
    },
};

export default widgetModule;
